import {ICreateOpts, IERC20, ITwoKeyBase, ITwoKeyHelpers, ITwoKeyUtils, IUpgradableExchange} from '../interfaces';
import {
    IAcquisitionCampaign,
    IAcquisitionCampaignMeta, IAddressStats, IConstantsLogicHandler, IConversionObject, IConversionStats,
    IConvertOpts,
    IJoinLinkOpts,
    IPublicLinkKey,
    IPublicLinkOpts,
    IReferrerSummary,
    ITokenAmount,
    ITwoKeyAcquisitionCampaign,
    IPublicMeta,
    IOffchainData, IContractorBalance, IGetStatsOpts, IInventoryStatus, IPrivateMetaInformation, IPossibleFiatReward,
    IPurchaseInformation, IRequiredRewards, IReferralLink, IContractorLink,
} from './interfaces';

import { BigNumber } from 'bignumber.js/bignumber';
import acquisitionContracts from '../contracts/acquisition';
import { promisify } from '../utils/promisify';
import { ISign } from '../sign/interface';
import {ITwoKeyFactory} from "../factory/interfaces";
import {gasLimit} from "../utils/helpers";
import { CREATE_CAMPAIGN } from '../constants'

/**
 *
 * @param {number[]} cuts
 * @param {number} maxPi
 * @returns {number}
 */
function calcFromCuts(cuts: number[], maxPi: number) {
    let referrerRewardPercent: number = maxPi;
    // we have all the cuts up to us. calculate our maximal bounty
    for (let i = 0; i < cuts.length; i++) {
        let cut = cuts[i];
        // calculate bounty after taking the part for the i-th influencer
        if ((0 < cut) && (cut <= 101)) {
            cut--;
            referrerRewardPercent *= (100. - cut) / 100.;
        } else {  // cut = 0 or 255 inidicate equal divistion down stream
            let n = cuts.length - i + 1; // how many influencers including us will split the bounty
            referrerRewardPercent *= (n - 1.) / n;
        }
    }
    return referrerRewardPercent;
}

export default class AcquisitionCampaign implements ITwoKeyAcquisitionCampaign {
    public readonly nonSingletonsHash: string;
    private readonly base: ITwoKeyBase;
    private readonly helpers: ITwoKeyHelpers;
    private readonly utils: ITwoKeyUtils;
    private readonly erc20: IERC20;
    private readonly twoKeyFactory: ITwoKeyFactory;
    private readonly sign: ISign;
    private readonly upgradableExchange: IUpgradableExchange;

    private AcquisitionCampaign: any;
    private AcquisitionConversionHandler: any;
    private AcquisitionLogicHandler: any;



    constructor(
        twoKeyProtocol: ITwoKeyBase,
        helpers: ITwoKeyHelpers,
        utils: ITwoKeyUtils,
        erc20: IERC20,
        sign: ISign,
        twoKeyFactory: ITwoKeyFactory,
        upgradableExchange: IUpgradableExchange,
    ) {
        this.base = twoKeyProtocol;
        this.helpers = helpers;
        this.utils = utils;
        this.erc20 = erc20;
        this.sign = sign;
        this.twoKeyFactory = twoKeyFactory;
        this.nonSingletonsHash = acquisitionContracts.NonSingletonsHash;
        this.upgradableExchange = upgradableExchange;
    }s

    /**
     *
     * @returns {{private_key: string; public_address: string}}
     */
    generatePublicMeta(): IPublicMeta {
        let pk = this.sign.generatePrivateKey();
        let public_address = this.sign.privateToPublic(Buffer.from(pk,'hex'));
        const private_key = pk;
        return {private_key, public_address};
    }

    /**
     *
     * @returns {string}
     */
    public getNonSingletonsHash() : string {
        return this.nonSingletonsHash;
    }



    async _getCampaignInstance(campaign: any, skipCache?: boolean): Promise<any> {
        const address = campaign.address || campaign;
        this.base._log('Requesting TwoKeyAcquisitionCampaignERC20 at', address);
        if (campaign.address) {
            return campaign;
        } else {
            return (await this.helpers._createAndValidate(acquisitionContracts.TwoKeyAcquisitionCampaignERC20.abi, campaign));
        }
    }

    async _getConversionHandlerInstance(campaign: any): Promise<any> {
        const acquisitionInstance = await this._getCampaignInstance(campaign);
        const conversionHandlerAddress = await promisify(acquisitionInstance.conversionHandler, []);
        return this.base.web3.eth.contract(acquisitionContracts.TwoKeyConversionHandler.abi).at(conversionHandlerAddress);
    }

    async _getLogicHandlerInstance(campaign: any): Promise<any> {
        const acquisitionInstance = await this._getCampaignInstance(campaign);
        const logicHandlerAddress = await promisify(acquisitionInstance.logicHandler, []);
        return this.base.web3.eth.contract(acquisitionContracts.TwoKeyAcquisitionLogicHandler.abi).at(logicHandlerAddress);
    }

    async _getPurchasesHandlerInstance(campaign: any) : Promise<any> {
        const conversionHandlerInstance = await this._getConversionHandlerInstance(campaign);
        const purchasesHandlerAddress = await promisify(conversionHandlerInstance.twoKeyPurchasesHandler,[]);
        return this.base.web3.eth.contract(acquisitionContracts.TwoKeyPurchasesHandler.abi).at(purchasesHandlerAddress);
    }

    /**
     *
     * @param {IAcquisitionCampaign} data
     * @param {string} from
     * @param {ICreateCampaignProgress} progressCallback
     * @param {number} gasPrice
     * @param {number} interval
     * @param {number} timeout
     * @returns {Promise<IAcquisitionCampaignMeta>}
     */
    public create(data: IAcquisitionCampaign, publicMeta: any, privateMeta: any, from: string, {progressCallback, gasPrice, interval, timeout = 60000}: ICreateOpts = {}): Promise<IAcquisitionCampaignMeta> {
        return new Promise(async (resolve, reject) => {
            try {
                if (this.nonSingletonsHash !== this.base.nonSingletonsHash) {
                    reject(new Error('To start new campaign please switch to latest version of AcquisitionSubmodule'));
                    return;
                }

                const incentiveModels = ['MANUAL', 'VANILLA_AVERAGE', 'VANILLA_AVERAGE_LAST_3X', 'VANILLA_POWER_LAW', 'NO_REFERRAL_REWARD'];
                const vestingAmounts = ['BONUS', 'BASE_AND_BONUS'];

                const selectedModel = incentiveModels.indexOf(data.incentiveModel);
                const selectedVestingAmount = vestingAmounts.indexOf(data.vestingAmount);


                if(selectedModel == -1 || selectedVestingAmount == -1) {
                    reject(new Error("Wrong incentive model passed or vesting amount option!"));
                    return;
                }


                let addresses = [
                    data.assetContractERC20,
                    data.moderator || this.base.twoKeyAdmin.address
                ];

                let conversionHandlerData = [
                    typeof(data.expiryConversion) == 'number' ? data.expiryConversion : 96,
                    data.isFiatConversionAutomaticallyApproved ? 1 : 0,
                    data.tokenDistributionDate,
                    data.maxDistributionDateShiftInDays,
                    data.numberOfVestingPortions,
                    data.numberOfDaysBetweenPortions,
                    data.bonusTokensVestingStartShiftInDaysFromDistributionDate,
                    selectedVestingAmount,
                    data.isKYCRequired ? 1 : 0,
                    data.maxConverterBonusPercentWei,
                ];

                let logicHandlerData = [
                        data.minContributionETHWei,
                        data.maxContributionETHWei,
                        data.pricePerUnitInETHWei,
                        data.campaignStartTime,
                        data.campaignEndTime,
                        data.maxConverterBonusPercentWei,
                        selectedModel,
                        data.isFiatOnly ? 1 : 0,
                        data.campaignHardCapWEI,
                        data.endCampaignWhenHardCapReached? 1 : 0,
                        data.campaignSoftCapWEI,
                ];


                let campaignData = [
                    data.maxReferralRewardPercentWei,
                    data.referrerQuota || 1000,
                    data.isKYCRequired ? 1 : 0,
                    data.mustConvertToReferr ? 1 : 0,
                    data.totalSupplyArcs || 10000000000
                ];

                if(!data.tokenDistributionDate) {
                    reject(new Error(`Token distribution date not passed, ${data.tokenDistributionDate}`));
                    return;
                }

                let {campaignAddress, conversionHandlerAddress, twoKeyAcquisitionLogicHandlerAddress, twoKeyPurchasesHandler} =
                    await this.twoKeyFactory.createProxiesForAcquisitions(
                        addresses,
                        conversionHandlerData,
                        logicHandlerData,
                        campaignData,
                        data.currency,
                        this.nonSingletonsHash,
                        from,
                        { progressCallback, gasPrice, interval, timeout, create_proxies_tx_hash: data.create_proxies_tx_hash}
                    );

                const { link: campaignPublicLinkKey, public_address, fSecret } = await this.generateContractorPublicLink(campaignAddress, from, progressCallback);

                //Creating private meta hash
                const privateMetaHash = await this.createPrivateMetaHash({ ...privateMeta, campaignPublicLinkKey, fSecret }, from);

                //Extra data which goes to public metasl
                let extraMetaInfo = {
                    contractor : from,
                    web3_address : campaignAddress,
                    conversionHandlerAddress,
                    twoKeyAcquisitionLogicHandlerAddress,
                    twoKeyPurchasesHandler,
                    ephemeralContractsVersion: this.nonSingletonsHash,
                };

                publicMeta = {...publicMeta, ...extraMetaInfo};

                //Stringify object and create public meta hash
                const dataString = typeof publicMeta === 'string' ? publicMeta : JSON.stringify(publicMeta);
                const publicMetaHash = await this.base.ipfs.add(dataString);


                let txHash = data.start_campaign_with_initial_params_tx_hash;
                let plasmaTxHash = data.set_public_link_key_plasma_tx_hash;

                if(!txHash || !plasmaTxHash) {
                    const hashes = await this.addKeysAndMetadataToContract(campaignAddress, publicMetaHash, privateMetaHash, public_address, from, gasPrice);

                    txHash = hashes.txHash;
                    plasmaTxHash = hashes.plasmaTxHash;

                }
                if (progressCallback) {
                    progressCallback(CREATE_CAMPAIGN.START_CAMPAIGN_WITH_INITIAL_PARAMS, false, txHash);
                    progressCallback(CREATE_CAMPAIGN.SET_PUBLIC_LINK_KEY_PLASMA, false, plasmaTxHash);
                }

                const promises = [
                    this.utils.getTransactionReceiptMined(txHash, {web3: this.base.web3, interval, timeout})
                ];

                if(plasmaTxHash) {
                    promises.push(this.utils.getTransactionReceiptMined(plasmaTxHash,{web3: this.base.plasmaWeb3, interval, timeout}));
                }

                await Promise.all(promises);

                if (progressCallback) {
                    progressCallback(CREATE_CAMPAIGN.START_CAMPAIGN_WITH_INITIAL_PARAMS, true, txHash);
                    progressCallback(CREATE_CAMPAIGN.SET_PUBLIC_LINK_KEY_PLASMA, true, plasmaTxHash);
                }

                resolve({
                    contractor: from,
                    campaignAddress,
                    conversionHandlerAddress,
                    twoKeyAcquisitionLogicHandlerAddress,
                    twoKeyPurchasesHandler,
                    campaignPublicLinkKey,
                    fSecret,
                    ephemeralContractsVersion: this.nonSingletonsHash,
                    publicMetaHash,
                    privateMetaHash
                });
            } catch (err) {
                reject(err);
            }
        });
    }


    /**
     *
     * @param campaign
     * @param {string} hash
     * @param {string} from
     * @param {number} gasPrice
     * @returns {Promise<string>}
     */
    public updateOrSetIpfsHashPublicMeta(campaign: any, hash: string, from: string, gasPrice: number = this.base._getGasPrice()): Promise<string> {
        return new Promise<string>(async (resolve, reject) => {
            try {
                const nonce = await this.helpers._getNonce(from);
                const twoKeyAcquisitionCampaign = await this._getCampaignInstance(campaign);
                const txHash: string = await promisify(twoKeyAcquisitionCampaign.updateIpfsHashOfCampaign, [hash, {
                    from,
                    gasPrice,
                    nonce,
                }]);
                resolve(txHash);
            } catch (e) {
                reject(e);
            }
        });
    }

//

    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<any>}
     */
    public getPublicMeta(campaign: any, from?: string): Promise<any> {
        return new Promise<any>(async (resolve, reject) => {
            try {
                const acquisitionCampaignInstance = await this._getCampaignInstance(campaign);
                const ipfsHash = await promisify(acquisitionCampaignInstance.publicMetaHash, []);
                const meta = await this.base.ipfs.get(ipfsHash, { json: true });
                resolve({ meta });
            } catch (e) {
                reject(e);
            }
        });
    }

    /**
     *
     * @param {string} hash
     * @param {string} from
     * @returns {Promise<any>}
     */
    public getCampaignFromLink(hash: string, from?: string): Promise<any> {
        return new Promise<any>(async (resolve, reject) => {
            try {
                const {campaign} = await this.base.ipfs.get(hash, { json: true });
                // await this.visit(campaign, hash);
                const campaignMeta = await this.getPublicMeta({campaign, from});
                resolve(campaignMeta);
            } catch (e) {
                reject(e);
            }
        });
    }

    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<number | string | BigNumber>}
     */
    public getInventoryBalance(campaign: any, from: string): Promise<number | string | BigNumber> {
        return new Promise<number>(async (resolve, reject) => {
            try {
                const logicHandlerInstance = await this._getLogicHandlerInstance(campaign);
                const balance = await promisify(logicHandlerInstance.getInventoryBalance, [{from}]);
                resolve(balance);
            } catch (e) {
                reject(e);
            }
        });
    }

    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<number | string | BigNumber>}
     */
    public async checkInventoryBalance(campaign: any, from: string): Promise<number | string | BigNumber> {
        return new Promise<number>(async(resolve,reject) => {
            try {
                const campaignInstance = await this._getCampaignInstance(campaign);
                let availableBalance = await promisify(campaignInstance.getAvailableAndNonReservedTokensAmount,[{from}]);
                resolve(availableBalance);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<number>}
     */
    public async getReferrerCut(campaign: any, from: string): Promise<number> {
        try {
            const campaignInstance = await this._getCampaignInstance(campaign);
            const cut = (await promisify(campaignInstance.getReferrerCut, [from, {from}])).toNumber() + 1;
            return Promise.resolve(cut);
        } catch (e) {
            Promise.reject(e);
        }
    }

    /**
     *
     * @param campaign
     * @param {string} from
     * @param {string} referralLink
     * @returns {Promise<number>}
     */
    public getEstimatedMaximumReferralReward(campaign: any, from: string, referralLink: string, fSecret?: string): Promise<number> {
        return new Promise(async (resolve, reject) => {
            try {
                if (!referralLink) {
                    const cut = await this.getReferrerCut(campaign, from);
                    resolve(cut);
                } else {
                    const { plasmaAddress } = this.base;
                    const campaignInstance = await this._getCampaignInstance(campaign);
                    const contractorAddress = await promisify(campaignInstance.contractor, []);
                    const offchainData = await this.base.ipfs.get(referralLink, { json: true });
                    const contractConstants = (await promisify(campaignInstance.getConstantInfo, []));
                    const { f_address, f_secret = fSecret, p_message } = offchainData;
                    const sig = this.sign.free_take(plasmaAddress, f_address, f_secret, p_message);

                    const maxReferralRewardPercent = contractConstants[1].toNumber();


                    if (f_address === contractorAddress) {
                        resolve(maxReferralRewardPercent);
                        return;
                    }
                    const firstAddressInChain = p_message ? `0x${p_message.substring(2, 42)}` : f_address;
                    let cuts: number[];
                    const firstPublicLink = await promisify(campaignInstance.publicLinkKeyOf, [firstAddressInChain]);
                    if (firstAddressInChain === contractorAddress) {
                        cuts = this.sign.validate_join(firstPublicLink, f_address, f_secret, sig, plasmaAddress);
                    } else {
                        cuts = (await promisify(campaignInstance.getReferrerCuts, [firstAddressInChain])).map(cut => cut.toNumber());
                        cuts = cuts.concat(this.sign.validate_join(firstPublicLink, f_address, f_secret, sig, plasmaAddress));
                    }
                    cuts.shift();
                    const estimatedMaxReferrerRewardPercent = calcFromCuts(cuts, maxReferralRewardPercent);
                    resolve(estimatedMaxReferrerRewardPercent);
                }
            } catch (e) {
                reject(e);
            }
        });
    }

    /* LINKS */

    /**
     *
     * @param {string} campaignAddress
     * @param {string} referralLink
     * @returns {Promise<string>}
     */
    public visit(campaignAddress: string, referralLink: string, fSecret?: string): Promise<string> {
        return new Promise<string>(async (resolve, reject) => {
            try {
                const { f_address, f_secret = fSecret, p_message } = await this.base.ipfs.get(referralLink, { json: true });
                const { plasmaAddress } = this.base;
                const sig = this.sign.free_take(plasmaAddress, f_address, f_secret, p_message);
                const campaignInstance = await this._getCampaignInstance(campaignAddress);
                const contractor = await promisify(campaignInstance.contractor, []);
                const joinedFrom = await promisify(this.base.twoKeyPlasmaEvents.getJoinedFrom, [campaignInstance.address, contractor, plasmaAddress]);
                const txHash: string = await promisify(this.base.twoKeyPlasmaEvents.visited, [
                    campaignInstance.address,
                    contractor,
                    sig,
                    {from: plasmaAddress, gasPrice: 0, gas: gasLimit}
                ]);
                if (!parseInt(joinedFrom, 16)) {
                    await this.utils.getTransactionReceiptMined(txHash, {web3: this.base.plasmaWeb3});
                    const note = await this.sign.encrypt(this.base.plasmaWeb3, plasmaAddress, f_secret, {plasma: true});
                    const noteTxHash = await promisify(this.base.twoKeyPlasmaEvents.setNoteByUser, [campaignInstance.address, note, {from: plasmaAddress, gas: gasLimit}]);
                }
                resolve(txHash);
            } catch (e) {
                reject(e);
            }
        });
    }

    /**
     * Function in charge to add public link key and public and private metadata to contract
     * @param campaign
     * @param {string} publicMetaHash
     * @param {string} privateMetaHash
     * @param {string} publicLink
     * @param {string} from
     * @returns {Promise<any>}
     */
    public addKeysAndMetadataToContract(campaign: any, publicMetaHash: string, privateMetaHash: string, publicLink:string, from: string, gasPrice = this.base._getGasPrice()) : Promise<any> {
        return new Promise<any>(async(resolve,reject) => {
            try {

                //Validate in any case the arguments are all properly set
                if(publicMetaHash.length == 46 && privateMetaHash.length == 46) {
                    const campaignInstance = await this._getCampaignInstance(campaign);
                    const [txHash, plasmaTxHash] = await Promise.all([
                        promisify(campaignInstance.startCampaignWithInitialParams, [
                            publicMetaHash,
                            privateMetaHash,
                            publicLink,
                            {
                                from,
                                gasPrice
                            }
                        ]),
                        this.helpers._awaitPlasmaMethod(promisify(this.base.twoKeyPlasmaEvents.setPublicLinkKey, [
                            campaignInstance.address,
                            from,
                            publicLink,
                            {from: this.base.plasmaAddress, gas: gasLimit}
                        ])),
                    ])
                    resolve({txHash,plasmaTxHash});
                } else {
                    reject(new Error("Either public or private meta hash are not good"));
                    return;
                }
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     * Function to stringify data and upload it to IPFS
     * @param campaign
     * @param data
     * @param {string} from
     * @returns {Promise}
     */
    public createPrivateMetaHash(data: any, from:string) : Promise<string> {
        return new Promise<string>(async(resolve,reject) => {
            try {
                //Convert data to string
                const dataString = typeof data === 'string' ? data : JSON.stringify(data);

                //Encrypt the string
                let encryptedData = await this.sign.encrypt(this.base.web3, from, dataString, {plasma:false});

                const hash = await this.base.ipfs.add(encryptedData);

                resolve(hash);
            } catch (e) {
                reject(e);
            }
        })
    }



    // Set Public Link
    /**
     *
     * @param campaign
     * @param {string} from
     * @param {string} publicLink
     * @param {number} cut
     * @param {number} gasPrice
     * @param {ICreateCampaignProgress} progressCallback
     * @param {number} interval
     * @param {number} timeout
     * @returns {Promise<IPublicLinkKey>}
     */
    public setPublicLinkKey(campaign: any, from: string,  publicLink: string, {
        cut,
        gasPrice = this.base._getGasPrice(),
        progressCallback,
        interval,
        timeout,
    }: IPublicLinkOpts = {}): Promise<IPublicLinkKey> {
        return new Promise(async (resolve, reject) => {
            try {
                const campaignInstance = await this._getCampaignInstance(campaign);
                const nonce = await this.helpers._getNonce(from);
                const contractor = await promisify(campaignInstance.contractor, [{from}]);
                const [txHash, plasmaTxHash] = await Promise.all([
                    promisify(campaignInstance.setPublicLinkKey, [
                        publicLink,
                        { from, nonce ,gasPrice }
                    ]),
                    this.helpers._awaitPlasmaMethod(promisify(this.base.twoKeyPlasmaEvents.setPublicLinkKey, [
                        campaignInstance.address,
                        contractor,
                        publicLink,
                        {from: this.base.plasmaAddress, gas: gasLimit}
                    ])),
                ]);
                if (progressCallback) {
                    progressCallback(CREATE_CAMPAIGN.SET_PUBLIC_LINK_KEY_CAMPAIGN, false, txHash);
                    progressCallback(CREATE_CAMPAIGN.SET_PUBLIC_LINK_KEY_PLASMA, false, plasmaTxHash);
                }

                const promises = [];
                promises.push(this.utils.getTransactionReceiptMined(txHash, { interval, timeout }));
                if (plasmaTxHash) {
                    promises.push(this.utils.getTransactionReceiptMined(plasmaTxHash, {web3: this.base.plasmaWeb3}));
                }

                await Promise.all(promises);

                if (progressCallback) {
                    if (plasmaTxHash) {
                        progressCallback(CREATE_CAMPAIGN.SET_PUBLIC_LINK_KEY_PLASMA, true, publicLink);
                    }
                    progressCallback(CREATE_CAMPAIGN.SET_PUBLIC_LINK_KEY_CAMPAIGN, true, publicLink);
                }
                if (cut != null) {
                    await promisify(campaignInstance.setCut, [cut, {from}]);
                }
                resolve({publicLink, contractor});
            } catch (err) {
                reject(err);
            }
        });
    }

    // Get Public Link
    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<string>}
     */
    public getPublicLinkKey(campaign: any, from: string): Promise<string> {
        return new Promise<string>(async(resolve,reject) => {
            try {
                const campaignInstance = await this._getCampaignInstance(campaign);
                const publicLink = await promisify(campaignInstance.publicLinkKeyOf, [from]);
                resolve(publicLink);
            } catch (e) {
                reject(e)
            }
        })
    }

    public generateContractorPublicLink(campaign: any, from: string, progressCallback?: any): Promise<IContractorLink> {
        return new Promise<any>(async(resolve,reject) => {
            try {
                const campaignAddress = typeof (campaign) === 'string' ? campaign
                    : (await this._getCampaignInstance(campaign)).address;
                const i = 1;
                const plasmaAddress = this.base.plasmaAddress;
                const msg = `0xdeadbeef${campaignAddress.slice(2)}${plasmaAddress.slice(2)}${i.toString(16)}`;
                const signedMessage = await this.sign.sign_message(this.base.plasmaWeb3, msg, plasmaAddress, { plasma: true });
                const private_key = this.base.web3.sha3(signedMessage).slice(2, 2 + 32 * 2);
                const public_address = '0x'+this.sign.privateToPublic(Buffer.from(private_key, 'hex'));

                const linkObject: IOffchainData = {
                    campaign: campaignAddress,
                    campaign_web3_address: campaignAddress,
                    'contractor': from,
                    f_address: plasmaAddress,
                    // f_secret: private_key,
                    ephemeralContractsVersion: this.nonSingletonsHash,
                    campaign_type: 'acquisition'
                };

                const link = await this.base.ipfs.add(linkObject);

                if (progressCallback) {
                    progressCallback(CREATE_CAMPAIGN.SET_IPFS_META, true, link);
                }
                resolve({ link, public_address, fSecret: private_key });
            } catch (e) {
                reject(e);
            }
        })
    }

    // Join Offchain
    /**
     *
     * @param campaign
     * @param {string} from
     * @param {number} cut
     * @param {number} gasPrice
     * @param {string} referralLink
     * @param {string} cutSign
     * @param {boolean} voting
     * @param {string} daoContract
     * @param {ICreateCampaignProgress} progressCallback
     * @param {number} interval
     * @param {number} timeout
     * @returns {Promise<string>}
     */
    public join(campaign: any, from: string, {
        cut,
        gasPrice = this.base._getGasPrice(),
        referralLink,
        fSecret,
        cutSign,
        voting,
        daoContract,
        progressCallback,
        interval,
        timeout,
        }: IJoinLinkOpts = {}): Promise<IReferralLink> {
        return new Promise(async (resolve, reject) => {
            try {
                const campaignAddress = typeof (campaign) === 'string' ? campaign
                    : (await this._getCampaignInstance(campaign)).address;
                const safeCut = this.sign.fixCut(cut);
                const i = 1;
                const plasmaAddress = this.base.plasmaAddress;
                const msg = `0xdeadbeef${campaignAddress.slice(2)}${plasmaAddress.slice(2)}${i.toString(16)}`;
                const signedMessage = await this.sign.sign_message(this.base.plasmaWeb3, msg, plasmaAddress, { plasma: true });
                const private_key = this.base.web3.sha3(signedMessage).slice(2, 2 + 32 * 2);
                const public_address = this.sign.privateToPublic(Buffer.from(private_key, 'hex'));
                let new_message;
                let contractor;
                let dao;
                if (referralLink) {
                    const {f_address, p_message, f_secret = fSecret, contractor: campaignContractor, dao: daoAddress} = await this.base.ipfs.get(referralLink, { json: true });
                    contractor = campaignContractor;
                    dao = daoAddress;
                    try {
                        const campaignInstance = await this._getCampaignInstance(campaignAddress);
                        const contractorAddress = await promisify(campaignInstance.contractor, []);
                        const plasmaAddress = this.base.plasmaAddress;
                        const sig = this.sign.free_take(plasmaAddress, f_address, f_secret, p_message);
                        const txHash = await this.helpers._awaitPlasmaMethod(promisify(this.base.twoKeyPlasmaEvents.joinCampaign, [campaignInstance.address, contractorAddress, sig, { from: plasmaAddress, gasPrice: 0, gas: gasLimit }]));
                        await this.utils.getTransactionReceiptMined(txHash, { web3: this.base.plasmaWeb3 });
                    } catch (e) {
                        // skip
                    }
                    new_message = this.sign.free_join(plasmaAddress, public_address, f_address, f_secret, p_message, safeCut, cutSign);
                } else {
                    const {contractor: campaignContractor} = await this.setPublicLinkKey(campaign, from, `0x${public_address}`, {
                        cut: safeCut,
                        gasPrice,
                        progressCallback,
                        interval,
                        timeout,
                    });
                    dao = voting ? daoContract : undefined;
                    contractor = campaignContractor;
                }
                const linkObject: IOffchainData = {
                    campaign: campaignAddress,
                    campaign_web3_address: campaignAddress,
                    contractor,
                    f_address: plasmaAddress,
                    // f_secret: private_key,
                    ephemeralContractsVersion: this.nonSingletonsHash,
                    campaign_type: 'acquisition',
                    dao,
                };
                if (new_message) {
                    linkObject.p_message = new_message;
                }
                const link = await this.base.ipfs.add(linkObject);
                resolve({ link, fSecret: private_key });
            } catch (err) {
                reject(err);
            }
        });
    }

    // ShortUrl
    /**
     *
     * @param campaign
     * @param {string} from
     * @param {string} referralLink
     * @param {number} cut
     * @param {number} gasPrice
     * @returns {Promise<string>}
     */
    public joinAndSetPublicLinkWithCut(campaign: any, from: string, referralLink: string, {cut, gasPrice = this.base._getGasPrice(), fSecret}: IPublicLinkOpts = {}): Promise<string> {
        return new Promise(async (resolve, reject) => {
            const {f_address, f_secret = fSecret, p_message} = await this.base.ipfs.get(referralLink, { json: true });
            if (!f_address || !f_secret) {
                reject('Broken Link');
            }
            try {
                const campaignInstance = await this._getCampaignInstance(campaign);
                let arcBalance = parseFloat((await promisify(campaignInstance.balanceOf, [from])).toString());
                const {public_address, private_key} = this.generatePublicMeta();

                if (!arcBalance) {
                    const signature = this.sign.free_join_take(from, public_address, f_address, f_secret, p_message, cut + 1);
                    const nonce = await this.helpers._getNonce(from);
                    const txHash: string = await promisify(campaignInstance.distributeArcsBasedOnSignature, [signature, {
                        from,
                        gasPrice,
                        nonce,
                    }]);
                    await this.utils.getTransactionReceiptMined(txHash);
                    arcBalance = parseFloat((await promisify(campaignInstance.balanceOf, [from])).toString());
                }
                if (arcBalance) {
                    const link = await this.base.ipfs.add({f_address: from, f_secret: private_key});
                    resolve(link);
                } else {
                    reject(new Error('Link is broken!'));
                }
            } catch (err) {
                reject(err);
            }
        });
    }

    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<number>}
     */
    public getBalanceOfArcs(campaign: any, from: string): Promise<number> {
        return new Promise(async (resolve, reject) => {
            try {
                const campaignInstance = await this._getCampaignInstance(campaign);
                const balance = (await promisify(campaignInstance.balanceOf, [from])).toNumber();
                resolve(balance);
            } catch (e) {
                reject(e);
            }
        });
    }

    /* PARTICIPATE */
    /**
     *
     * @param campaign
     * @param {boolean} isPaymentFiat
     * @param {string | number | BigNumber} value
     * @returns {Promise<ITokenAmount>}
     */
    public getEstimatedTokenAmount(campaign: any, isPaymentFiat: boolean, value: string | number | BigNumber): Promise<ITokenAmount> {
        return new Promise<ITokenAmount>(async (resolve, reject) => {
            try {
                const twoKeyAcquisitionLogicHandlerInstance = await this._getLogicHandlerInstance(campaign);
                const constants = await promisify(twoKeyAcquisitionLogicHandlerInstance.getConstantInfo, []);
                let [baseTokens, bonusTokens] = await promisify(twoKeyAcquisitionLogicHandlerInstance.getEstimatedTokenAmount, [value, isPaymentFiat]);
                baseTokens = this.utils.fromWei(baseTokens, constants[4]);
                baseTokens = parseFloat(baseTokens.toString());
                bonusTokens = this.utils.fromWei(bonusTokens, constants[4]);
                bonusTokens = parseFloat(bonusTokens.toString());
                resolve({
                    baseTokens,
                    bonusTokens,
                    totalTokens: baseTokens + bonusTokens,
                });
            } catch (e) {
                reject(e);
            }
        });
    }

    /**
     *
     * @param campaign
     * @param {string | number | BigNumber} value
     * @param {string} publicLink
     * @param {string} from
     * @param {number} gasPrice
     * @param {boolean} isConverterAnonymous
     * @returns {Promise<string>}
     */
    public joinAndConvert(campaign: any, value: string | number | BigNumber, publicLink: string, from: string, {gasPrice = this.base._getGasPrice(), isConverterAnonymous, fSecret}: IConvertOpts = {}): Promise<string> {
        return new Promise(async (resolve, reject) => {
            try {
                const { f_address, f_secret = fSecret, p_message } = await this.base.ipfs.get(publicLink, { json: true });
                if (!f_address || !f_secret) {
                    reject('Broken Link');
                }
                const campaignInstance = await this._getCampaignInstance(campaign);
                const prevChain = await promisify(campaignInstance.getReceivedFrom, [from]);
                const nonce = await this.helpers._getNonce(from);
                let txHash;
                if (!parseInt(prevChain, 16)) {
                    const plasmaAddress = this.base.plasmaAddress;
                    const signature = this.sign.free_take(plasmaAddress, f_address, f_secret, p_message);

                    const cuts = this.sign.validate_join(null, null, null, signature, plasmaAddress);
                    txHash = await promisify(campaignInstance.convert, [signature,false, {
                        from,
                        gasPrice,
                        value,
                        nonce,
                    }]);

                    try {
                        const contractor = await promisify(campaignInstance.contractor, []);
                        await this.helpers._awaitPlasmaMethod(promisify(this.base.twoKeyPlasmaEvents.joinCampaign, [campaignInstance.address, contractor, signature, { from: plasmaAddress, gasPrice: 0, gas: gasLimit }]));
                    } catch (e) {
                        // skip
                    }
                    resolve(txHash);
                } else {
                    const txHash: string = await promisify(campaignInstance.convert, ['0x0', false,{
                        from,
                        gasPrice,
                        value,
                        nonce,
                    }]);
                    resolve(txHash);
                }
            } catch (e) {
                this.base._log(e.toString());
                reject(e);
            }
        });
    }

    /**
     *
     * @param campaign
     * @param {string | number | BigNumber} value
     * @param {string} from
     * @param {number} gasPrice
     * @param {boolean} isConverterAnonymous
     * @returns {Promise<string>}
     */
    public convert(campaign: any, value: string | number | BigNumber, from: string, {gasPrice = this.base._getGasPrice(), isConverterAnonymous}: IConvertOpts = {}) : Promise<string> {
        return new Promise<string>(async(resolve,reject) => {
            try {
                const campaignInstance = await this._getCampaignInstance(campaign);
                const nonce = await this.helpers._getNonce(from);
                const txHash: string = await promisify(campaignInstance.convert, ['0x0',false,{
                    from,
                    gasPrice,
                    value,
                    nonce,
                }]);
                resolve(txHash);
            } catch (e) {
                reject(e);
            }
        })
    }

    // Send ARCS to other account
    /**
     *
     * @param campaign
     * @param {string} from
     * @param {string} referralLink
     * @param {string} recipient
     * @param {number} gasPrice
     * @returns {Promise<string>}
     */
    public joinAndShareARC(campaign: any, from: string, referralLink: string, recipient: string, {gasPrice = this.base._getGasPrice(), fSecret}: IPublicLinkOpts = {}): Promise<string> {
        return new Promise(async (resolve, reject) => {
            try {
                const {f_address, f_secret = fSecret, p_message} = await this.base.ipfs.get(referralLink, { json: true });
                if (!f_address || !f_secret) {
                    reject('Broken Link');
                }
                const campaignInstance = await this._getCampaignInstance(campaign);
                const arcBalance = parseFloat((await promisify(campaignInstance.balanceOf, [from])).toString());
                const prevChain = await promisify(campaignInstance.getReceivedFrom, [recipient]);
                if (parseInt(prevChain, 16)) {
                    reject(new Error('User already in chain'));
                }
                const nonce = await this.helpers._getNonce(from);
                if (!arcBalance) {
                    const {public_address} = this.generatePublicMeta();
                    const signature = this.sign.free_take(this.base.plasmaAddress, f_address, f_secret, p_message);
                    const txHash: string = await promisify(campaignInstance.joinAndShareARC, [signature, recipient, {
                        from,
                        gasPrice,
                        nonce,
                    }]);
                    resolve(txHash);
                } else {
                    const txHash: string = await promisify(campaignInstance.transfer, [recipient, 1, {
                        from,
                        gasPrice,
                        nonce,
                    }]);
                    resolve(txHash);
                }
            } catch (e) {
                reject(e);
            }
        });
    }

    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<boolean>}
     */
    public isAddressJoined(campaign: any, from: string): Promise<boolean> {
        return new Promise<boolean>(async (resolve, reject) => {
            try {
                const logicHandlerInstance = await this._getLogicHandlerInstance(campaign);
                resolve(await promisify(logicHandlerInstance.getAddressJoinedStatus, [from,{from}]));
            } catch (e) {
                reject(e);
            }
        });
    }

    /**
     * Function to get all conversion ids for the converter, can be called by converter itself, moderator or contractor
     * @param campaign
     * @param {string} converterAddress
     * @param {string} from
     * @returns {Promise<any>}
     */
    public getConverterConversionIds(campaign: any, converterAddress: string, from: string) : Promise<number[]> {
        return new Promise<number[]>(async(resolve,reject) => {
            try {
                const conversionHandlerInstance = await this._getConversionHandlerInstance(campaign);
                const conversionIds = await promisify(conversionHandlerInstance.getConverterConversionIds,[converterAddress,{from}]);
                resolve(conversionIds);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     * Function to get number of conversions, can be only called by contractor/moderator
     * @param campaign
     * @param {string} from
     * @returns {Promise<number>}
     */
    public getNumberOfConversions(campaign:any, from:string) : Promise<number> {
        return new Promise<number>(async(resolve,reject) => {
            try {
                const conversionHandlerInstance = await this._getConversionHandlerInstance(campaign);
                const numberOfConversions = await promisify(conversionHandlerInstance.getNumberOfConversions,[{from}]);
                resolve(numberOfConversions);
            } catch (e) {
                reject(e);
            }
        })
    }


    /**
     * Function which gets conversion object, if converter is anonymous will return empty address for him
     * @param campaign
     * @param {number} conversionId
     * @param {string} from
     * @returns {Promise<any>}
     */
    public getConversion(campaign: any, conversionId: number, from: string) : Promise<IConversionObject> {
        return new Promise<IConversionObject>(async(resolve,reject) => {
            try {
                const conversionHandlerInstance = await this._getConversionHandlerInstance(campaign);
                let contractor, contractorProceedsETHWei, converter, state, conversionAmount, maxReferralRewardEthOrFiat, maxReferralRewardTwoKey, moderatorFeeETHWei, baseTokenUnits,
                bonusTokenUnits, conversionCreatedAt, conversionExpiresAt, isConversionFiat;
                let hexedValues = await promisify(conversionHandlerInstance.getConversion,[conversionId,{from}]);
                contractor = hexedValues.slice(0, 42);
                contractorProceedsETHWei = parseInt(hexedValues.slice(42, 42+64),16);
                converter = '0x' + hexedValues.slice(42+64,42+64+40);
                state = parseInt(hexedValues.slice(42+64+40,42+64+40+2),16);
                conversionAmount = parseInt(hexedValues.slice(42+64+40+2,42+64+40+2+64),16);
                maxReferralRewardEthOrFiat = parseInt(hexedValues.slice(42+64+40+2+64,42+64+40+2+64+64),16);
                maxReferralRewardTwoKey = parseInt(hexedValues.slice(42+64+40+2+64+64,42+64+40+2+64+64+64),16);
                moderatorFeeETHWei = parseInt(hexedValues.slice(42+64+40+2+64+64+64,42+64+40+2+64+64+64+64),16);
                baseTokenUnits = parseInt(hexedValues.slice(42+64+40+2+64+64+64+64,42+64+40+2+64+64+64+64+64),16);
                bonusTokenUnits = parseInt(hexedValues.slice(42+64+40+2+64+64+64+64+64,42+64+40+2+64+64+64+64+64+64),16);
                conversionCreatedAt = parseInt(hexedValues.slice(42+64+40+2+64+64+64+64+64+64,42+64+40+2+64+64+64+64+64+64+64),16);
                conversionExpiresAt = parseInt(hexedValues.slice(42+64+40+2+64+64+64+64+64+64+64,42+64+40+2+64+64+64+64+64+64+64+64),16);
                isConversionFiat = parseInt(hexedValues.slice(42+64+40+2+64+64+64+64+64+64+64+64,42+64+40+2+64+64+64+64+64+64+64+2+64),16) == 1;


                if(state == 0) {
                    state = 'PENDING_APPROVAL';
                } else if(state == 1) {
                    state = 'APPROVED';
                } else if(state == 2) {
                    state = 'EXECUTED';
                } else if(state == 3) {
                    state = 'REJECTED';
                } else if(state == 4) {
                    state = 'CANCELLED_BY_CONVERTER';
                }


                let obj : IConversionObject = {
                    'contractor' : contractor,
                    'contractorProceedsETHWei' : parseFloat(this.utils.fromWei(contractorProceedsETHWei, 'ether').toString()),
                    'converter' : converter,
                    'state' : state.toString(),
                    'conversionAmount' : parseFloat(this.utils.fromWei(conversionAmount, 'ether').toString()),
                    'maxReferralRewardEthOrFiat' : parseFloat(this.utils.fromWei(maxReferralRewardEthOrFiat, 'ether').toString()),
                    'maxReferralReward2key' : parseFloat(this.utils.fromWei(maxReferralRewardTwoKey, 'ether').toString()),
                    'moderatorFeeETHWei' : parseFloat(this.utils.fromWei(moderatorFeeETHWei, 'ether').toString()),
                    'baseTokenUnits' : parseFloat(this.utils.fromWei(baseTokenUnits, 'ether').toString()),
                    'bonusTokenUnits' : parseFloat(this.utils.fromWei(bonusTokenUnits, 'ether').toString()),
                    'conversionCreatedAt' : conversionCreatedAt,
                    'conversionExpiresAt' : conversionExpiresAt,
                    'isConversionFiat' : isConversionFiat,
                };
                resolve(obj);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {boolean} skipCache
     * @returns {Promise<string>}
     */
    public getTwoKeyConversionHandlerAddress(campaign: any, skipCache?: boolean): Promise<string> {
        return new Promise(async (resolve, reject) => {
            try {
                const campaignInstance = await this._getCampaignInstance(campaign, skipCache);
                const conversionHandler = await promisify(campaignInstance.conversionHandler, []);
                resolve(conversionHandler);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     * Can only be called by contractor or moderator
     * @param campaign
     * @param {string} from
     * @returns {Promise<string[]>}
     */
    public getAllPendingConverters(campaign: any, from: string): Promise<string[]> {
        return new Promise(async (resolve, reject) => {
            try {
                const conversionHandlerInstance = await this._getConversionHandlerInstance(campaign);
                const stateHex = "0x50454e44494e475f415050524f56414c00000000000000000000000000000000";
                const pendingConverters = await promisify(conversionHandlerInstance.getAllConvertersPerState, [stateHex,{from}]);
                resolve(pendingConverters);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<string[]>}
     */
    public getApprovedConverters(campaign: any, from: string): Promise<string[]> {
        return new Promise(async (resolve, reject) => {
            try {
                const conversionHandlerInstance = await this._getConversionHandlerInstance(campaign);
                const stateHex = "0x415050524f564544000000000000000000000000000000000000000000000000";
                const approvedConverters = await promisify(conversionHandlerInstance.getAllConvertersPerState, [stateHex,{from}]);
                resolve(approvedConverters);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<string[]>}
     */
    public getAllRejectedConverters(campaign: any, from: string): Promise<string[]> {
        return new Promise(async (resolve, reject) => {
            try {
                const conversionHandlerInstance = await this._getConversionHandlerInstance(campaign);
                const stateHex = "0x52454a4543544544000000000000000000000000000000000000000000000000";
                const rejectedConverters = await promisify(conversionHandlerInstance.getAllConvertersPerState, [stateHex,{from}]);
                resolve(rejectedConverters);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} converter
     * @param {string} from
     * @param {number} gasPrice
     * @returns {Promise<string>}
     */
    public approveConverter(campaign: any, converter: string, from: string, gasPrice: number = this.base._getGasPrice()): Promise<string> {
        return new Promise(async (resolve, reject) => {
            try {
                const nonce = await this.helpers._getNonce(from);
                const conversionHandlerInstance = await this._getConversionHandlerInstance(campaign);
                const txHash: string = await promisify(conversionHandlerInstance.approveConverter, [converter, {
                    from,
                    gasPrice,
                    nonce
                }]);
                resolve(txHash);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} converter
     * @param {string} from
     * @param {number} gasPrice
     * @returns {Promise<string>}
     */
    public rejectConverter(campaign: any, converter: string, from: string, gasPrice: number = this.base._getGasPrice()): Promise<string> {
        return new Promise(async (resolve, reject) => {
            try {
                const nonce = await this.helpers._getNonce(from);
                const conversionHandlerInstance = await this._getConversionHandlerInstance(campaign);
                const txHash: string = await promisify(conversionHandlerInstance.rejectConverter, [converter, {
                    from,
                    gasPrice,
                    nonce
                }]);
                resolve(txHash);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {number} conversion_id
     * @param {string} from
     * @param {number} gasPrice
     * @returns {Promise<string>}
     */
    public executeConversion(campaign: any, conversion_id: number, from: string, gasPrice: number = this.base._getGasPrice()): Promise<string> {
        return new Promise(async (resolve, reject) => {
            try {
                const conversionHandlerAddress = await this.getTwoKeyConversionHandlerAddress(campaign);
                const conversionHandlerInstance = this.base.web3.eth.contract(acquisitionContracts.TwoKeyConversionHandler.abi).at(conversionHandlerAddress);
                const nonce = await this.helpers._getNonce(from);
                const txHash: string = await promisify(conversionHandlerInstance.executeConversion, [conversion_id, {
                    from,
                    gasPrice,
                    nonce
                }]);
                resolve(txHash);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     * Function where converter can cancel by himself one of his conversions which is still pending approval
     * @param campaign
     * @param {number} conversion_id
     * @param {string} from
     * @param {number} gasPrice
     * @returns {Promise<string>}
     */
    public converterCancelConversion(campaign: any, conversion_id: number, from: string, gasPrice: number = this.base._getGasPrice()) : Promise<string> {
        return new Promise<string>(async(resolve,reject) => {
            try {
                const conversionHandlerAddress = await this.getTwoKeyConversionHandlerAddress(campaign);
                const conversionHandlerInstance = this.base.web3.eth.contract(acquisitionContracts.TwoKeyConversionHandler.abi).at(conversionHandlerAddress);
                const nonce = await this.helpers._getNonce(from);
                const txHash: string = await promisify(conversionHandlerInstance.converterCancelConversion,[conversion_id, {
                    from,
                    gasPrice,
                    nonce
                }]);
                resolve(txHash);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} converter
     * @param {string} from
     * @param {boolean} skipCache
     * @returns {Promise<string[]>}
     */
    public getLockupContractsForConverter(campaign: any, converter: string, from: string, skipCache?: boolean): Promise<string[]> {
        return new Promise(async (resolve, reject) => {
            try {
                const conversionHandlerAddress = await this.getTwoKeyConversionHandlerAddress(campaign, skipCache);
                const conversionHandlerInstance = this.base.web3.eth.contract(acquisitionContracts.TwoKeyConversionHandler.abi).at(conversionHandlerAddress);
                const lockupContractAddresses = await promisify(conversionHandlerInstance.getLockupContractsForConverter, [converter, {from}]);
                resolve(lockupContractAddresses);
            } catch (e) {
                reject(e);
            }
        });
    }



    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<boolean>}
     */
    public isAddressContractor(campaign: any, from: string): Promise<boolean> {
        return new Promise(async (resolve, reject) => {
            try {
                const campaignInstance = await this._getCampaignInstance(campaign);
                const result: string = await promisify(campaignInstance.contractor, [{from}]);
                resolve(result === from);
            } catch (e) {
                reject(e);
            }
        })
    }




    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<number>}
     */
    public getAmountOfEthAddressSentToAcquisition(campaign: any, from: string): Promise<number> {
        return new Promise<number>(async (resolve, reject) => {
            try {
                const campaignInstance = await this._getCampaignInstance(campaign);
                let [value,x]= await promisify(campaignInstance.getAmountAddressSent, [from]);
                value = parseFloat(this.utils.fromWei(value, 'ether').toString());
                resolve(value);
            } catch (e) {
                reject(e);
            }
        });
    }


    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<number>}
     */
    public howMuchUserCanContribute(campaign:any, from: string) : Promise<number> {
        return new Promise<number>(async(resolve,reject) => {
            try {
                const logicHandlerInstance = await this._getLogicHandlerInstance(campaign);
                let [canContribute, leftToContribute] = await promisify(logicHandlerInstance.validateMinMaxContributionForETHConversion,[from,0,{from}]);
                let parsed = parseFloat(this.utils.fromWei(leftToContribute,'ether').toString());
                resolve(parsed);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} from
     * @param {number} gasPrice
     * @returns {Promise<string>}
     */
    public contractorWithdraw(campaign: any, from: string, gasPrice: number = this.base._getGasPrice()): Promise<string> {
        return new Promise<string>(async (resolve, reject) => {
            try {

                let balance = await this.getContractorBalance(campaign, from);
                if(balance.available > 0) {
                    const nonce = await this.helpers._getNonce(from);
                    const campaignInstance = await this._getCampaignInstance(campaign);
                    const txHash: string = await promisify(campaignInstance.withdrawContractor, [{
                        from,
                        gasPrice,
                        nonce
                    }]);
                    resolve(txHash);
                } else {
                    reject(new Error('Contractor does not have any balance or he has already withdrawn his earnings.'));
                }

            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<number>}
     */
    public getContractorBalance(campaign: any, from: string): Promise<IContractorBalance> {
        return new Promise<IContractorBalance>(async (resolve, reject) => {
            try {
                const campaignInstance = await this._getCampaignInstance(campaign);
                let [available, total] = await promisify(campaignInstance.getContractorBalanceAndTotalProceeds, [{from}]);
                available = parseFloat(this.utils.fromWei(available, 'ether').toString());
                total = parseFloat(this.utils.fromWei(total, 'ether').toString());
                resolve({ available, total });
            } catch (e) {
                reject(e);
            }
        })
    }


    /**
     *
     * @param campaign
     * @param {string} from
     * @param {number} gasPrice
     * @returns {Promise<any>}
     */
    public moderatorAndReferrerWithdraw(campaign: any, withdraw_as_stable:Boolean, from: string, gasPrice: number = this.base._getGasPrice()) : Promise<string> {
        return new Promise<string>(async(resolve,reject) => {
            try {
                /**
                 * @type {number}
                 */
                const nonce = await this.helpers._getNonce(from);
                const campaignInstance = await this._getCampaignInstance(campaign);
                const txHash: string = await promisify(campaignInstance.referrerWithdraw,[from,withdraw_as_stable,
                    {
                        from,
                        gasPrice,
                        nonce
                    }]);
                resolve(txHash);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<string>}
     */
    public getModeratorAddress(campaign: any, from: string) : Promise<string> {
        return new Promise(async(resolve,reject) => {
           try {
                const campaignInstance = await this._getCampaignInstance(campaign);
                const moderator: string = await promisify(campaignInstance.moderator,[{from}]);
                resolve(moderator);
           } catch (e) {
               reject(e);
           }
        });
    }


    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<string>}
     */
    public getAcquisitionCampaignCurrency(campaign: any, from: string) : Promise<string> {
        return new Promise<string>(async(resolve, reject) => {
            try {
                const twoKeyAcquisitionLogicHandlerInstance = await this._getLogicHandlerInstance(campaign);
                const currency: string = await promisify(twoKeyAcquisitionLogicHandlerInstance.currency,[{from}]);
                resolve(currency);
            } catch (e) {
                reject(e);
            }
        });
    }

    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<number>}
     */
    public getModeratorTotalEarnings(campaign:any, from:string) : Promise<number> {
        return new Promise<number>(async(resolve,reject) => {
            try {
                const campaignInstance = await this._getCampaignInstance(campaign);
                let moderatorTotalEarnings= await promisify(campaignInstance.getModeratorTotalEarnings,[{from}]);
                moderatorTotalEarnings = parseFloat(this.utils.fromWei(moderatorTotalEarnings, 'ether').toString());
                resolve(moderatorTotalEarnings);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} signature
     * @param {boolean} skipCache
     * @returns {Promise<IReferrerSummary>}
     */
    public getReferrerBalanceAndTotalEarningsAndNumberOfConversions(campaign:any, signature: string, skipCache?: boolean) : Promise<IReferrerSummary> {
        return new Promise<any>(async(resolve,reject) => {
           try {
               const campaignInstance = await this._getLogicHandlerInstance(campaign);
               let [referrerBalanceAvailable, referrerTotalEarnings, referrerInCountOfConversions, contributions] =
                   await promisify(campaignInstance.getReferrerBalanceAndTotalEarningsAndNumberOfConversions,['0x0',signature, []]);
               const obj = {
                   balanceAvailable: parseFloat(this.utils.fromWei(referrerBalanceAvailable, 'ether').toString()),
                   totalEarnings: parseFloat(this.utils.fromWei(referrerTotalEarnings, 'ether').toString()),
                   numberOfConversionsParticipatedIn : parseFloat(referrerInCountOfConversions.toString()),
                   campaignAddress: campaignInstance.address,
                   rewardsPerConversions: contributions.map(item => parseFloat(this.utils.fromWei(item, 'ether').toString())),
               };
               resolve(obj)
           } catch (e) {
               reject(e);
           }
        });
    }

    /**
     *
     * @param campaign
     * @returns {Promise<IInventoryStatus>}
     */
    public getInventoryStatus(campaign:any) : Promise<IInventoryStatus> {
        return new Promise<IInventoryStatus>(async(resolve,reject) => {
           try {
               const campaignInstance = await this._getCampaignInstance(campaign);
               let [totalBalance, reservedForConverters, reservedForReferrerRewards, rewardsForFiatConversionsAvailable] = await promisify(campaignInstance.getInventoryStatus,[]);
               const obj: IInventoryStatus = {
                   totalBalance: parseFloat(this.utils.fromWei(totalBalance, 'ether').toString()),
                   reservedForConverters: parseFloat(this.utils.fromWei(reservedForConverters, 'ether').toString()),
                   reservedForReferrerRewards: parseFloat(this.utils.fromWei(reservedForReferrerRewards, 'ether').toString()),
                   rewardsForFiatConversionsAvailable: parseFloat(this.utils.fromWei(rewardsForFiatConversionsAvailable, 'ether').toString())
               };
               resolve(obj);
           } catch (e) {
               reject(e);
           }
        });
    }


    /**
     *
     * @param campaign
     * @param {string} signature
     * @param {number[]} conversionIds
     * @param {boolean} skipCache
     * @returns {Promise<number[]>}
     */
    public getReferrerRewardsPerConversion(campaign:any, signature: string, conversionIds: number[], skipCache?: boolean) : Promise<number[]> {
        return new Promise<number[]>(async(resolve,reject) => {
            try {
                const campaignInstance = await this._getLogicHandlerInstance(campaign);
                let [,,,contributionsPerReferrer] =
                    await promisify(campaignInstance.getReferrerBalanceAndTotalEarningsAndNumberOfConversions,['0x0',signature, conversionIds]);
                for(let i=0; i<contributionsPerReferrer.length; i++) {
                    contributionsPerReferrer[i] = parseFloat(this.utils.fromWei(contributionsPerReferrer[i], 'ether').toString())
                }
                resolve(contributionsPerReferrer);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     * This function will get available balance including 'reserved' tokens
     * @param campaign
     * @param {string} from
     * @returns {Promise<number>}
     */
    public getCurrentAvailableAmountOfTokens(campaign:any, from:string) : Promise<number> {
        return new Promise(async(resolve,reject) => {
            try {
                const campaignInstance = await this._getCampaignInstance(campaign);
                let availableBalance = await promisify(campaignInstance.getAvailableAndNonReservedTokensAmount,[{from}]);
                resolve(parseFloat(this.utils.fromWei(availableBalance, 'ether').toString()));
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     * Only contractor or moderator can set it
     * @param campaign
     * @param {string} privateMetaHash
     * @param {string} from
     * @returns {Promise<string>}
     */
    public setPrivateMetaHash(campaign: any, data: any, from:string) : Promise<string> {
        return new Promise<string>(async(resolve,reject) => {
            try {
                //Convert data to string
                const dataString = typeof data === 'string' ? data : JSON.stringify(data);

                //Encrypt the string
                let encryptedString = await this.sign.encrypt(this.base.web3, from, dataString, {plasma:false});

                const hash = await this.base.ipfs.add(encryptedString);


                const acquisitionCampaignInstance = await this._getCampaignInstance(campaign);
                let txHash: string = await promisify(acquisitionCampaignInstance.updateOrSetPrivateMetaHash,[hash,{from}]);
                resolve(txHash);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     * Only contractor or moderator can get it
     * @param campaign
     * @param {string} from
     * @returns {Promise<string>}
     */
    public getPrivateMetaHash(campaign: any, from: string) : Promise<IPrivateMetaInformation> {
        return new Promise<IPrivateMetaInformation>(async(resolve,reject) => {
            try {
                const acquisitionCampaignInstance = await this._getCampaignInstance(campaign);
                let ipfsHash: string = await promisify(acquisitionCampaignInstance.privateMetaHash,[{from}]);

                let contractor: string = await promisify(acquisitionCampaignInstance.contractor,[]);

                //Validate that the sender is contractor
                if(from != contractor) {
                    reject('This can be decrypted only by contractor');
                } else {
                    const privateHashEncrypted = await this.base.ipfs.get(ipfsHash);

                    let privateMetaHashDecrypted = await this.sign.decrypt(this.base.web3,from,privateHashEncrypted,{plasma : false});

                    resolve(JSON.parse(privateMetaHashDecrypted.slice(2)));
                }
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     * Gets stats from conversion handler contract
     * @param campaign
     * @param {string} from
     * @returns {Promise<IConversionStats>}
     */
    public getCampaignSummary(campaign: any, from: string) : Promise<IConversionStats> {
        return new Promise<IConversionStats>(async(resolve,reject) => {
            try {
                const conversionHandlerInstance = await this._getConversionHandlerInstance(campaign);

                const [
                    pending,
                    approved,
                    rejected,
                    counters
                ] = await promisify(conversionHandlerInstance.getCampaignSummary,[{from}]);

                let pendingConversions = counters[0].toNumber();
                let approvedConversions = counters[1].toNumber();
                let rejectedConversions = counters[2].toNumber();
                let executedConversions = counters[3].toNumber();
                let cancelledConversions = counters[4].toNumber();
                let uniqueConverters = counters[5].toNumber();
                let raisedFundsEthWei = parseFloat(this.utils.fromWei(counters[6], 'ether').toString());
                let tokensSold = parseFloat(this.utils.fromWei(counters[7], 'ether').toString());
                let totalBounty = parseFloat(this.utils.fromWei(counters[8], 'ether').toString());
                let raisedFundsFiatWei = parseFloat(this.utils.fromWei(counters[9], 'ether').toString());

                let campaignRaisedByNow = 0;
                if(counters.length == 11) {
                    campaignRaisedByNow = parseFloat(this.utils.fromWei(counters[10], 'ether').toString());
                }


                resolve(
                    {
                        pendingConverters:  pending.toNumber(),
                        approvedConverters:  approved.toNumber(),
                        rejectedConverters:  rejected.toNumber(),
                        pendingConversions,
                        approvedConversions,
                        rejectedConversions,
                        executedConversions,
                        cancelledConversions,
                        uniqueConverters,
                        raisedFundsEthWei,
                        tokensSold,
                        totalBounty,
                        raisedFundsFiatWei,
                        campaignRaisedByNow
                    }
                )
            } catch (e) {
                reject(e);
            }
        })
    }



    /**
     * This is method to get constant values from acquisition logic handler
     * @param campaign
     * @returns {Promise<IConstantsLogicHandler>}
     */
    public getConstantsFromLogicHandler(campaign:any) : Promise<IConstantsLogicHandler> {
        return new Promise<IConstantsLogicHandler>(async(resolve,reject) => {
            try {
                const twoKeyAcquisitionLogicHandlerInstance = await this._getLogicHandlerInstance(campaign);
                let [
                        campaignStartTime,
                        campaignEndTime,
                        minContributionETHorFiatCurrency,
                        maxContributionETHorFiatCurrency,
                        unit_decimals,
                        pricePerUnitInETHWeiOrUSD,
                        maxConverterBonusPercent
                ] = await promisify(twoKeyAcquisitionLogicHandlerInstance.getConstantInfo,[]);

                let obj : IConstantsLogicHandler = {
                    campaignStartTime: campaignStartTime.toNumber(),
                    campaignEndTime: campaignEndTime.toNumber(),
                    minContributionETHorFiatCurrency: parseFloat(this.utils.fromWei(minContributionETHorFiatCurrency, 'ether').toString()),
                    maxContributionETHorFiatCurrency: parseFloat(this.utils.fromWei(maxContributionETHorFiatCurrency, 'ether').toString()),
                    unit_decimals: unit_decimals.toNumber(),
                    pricePerUnitInETHWeiOrUSD: parseFloat(this.utils.fromWei(pricePerUnitInETHWeiOrUSD, 'ether').toString()),
                    maxConverterBonusPercent: maxConverterBonusPercent,
                };

                resolve(obj);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} address
     * @param {boolean} plasma
     * @returns {Promise<IAddressStats>}
     */
    public getAddressStatistic(campaign: any, address: string, signature: string, {from , plasma = false} : IGetStatsOpts = {}) : Promise<IAddressStats>{
        return new Promise<IAddressStats>(async(resolve,reject) => {
            try {
                const twoKeyAcquisitionLogicHandlerInstance = await this._getLogicHandlerInstance(campaign);

                let username, fullname, email;
                let hex= await promisify(twoKeyAcquisitionLogicHandlerInstance.getSuperStatistics,[address, plasma, signature,{from}]);
                /**
                 *
                 * Unpack bytes for statistics
                 */
                username = hex.slice(0,66);
                fullname = hex.slice(66,66+64);
                email = hex.slice(66+64,66+64+64);


                let isJoined = parseInt(hex.slice(66+64+64,66+64+64+2),16) == 1;
                let ethereumof = '0x' + hex.slice(66+64+64+2, 66+64+64+2+40);
                let amountConverterSpent = parseInt(hex.slice(66+64+64+2+40, 66+64+64+2+40+64),16);
                let rewards = parseInt(hex.slice(66+64+64+2+40+64,66+64+64+2+40+64+64),16);
                let unitsConverterBought = parseInt(hex.slice(66+64+64+2+40+64+64,66+64+64+2+40+64+64+64),16);
                let isConverter = parseInt(hex.slice(66+64+64+2+40+64+64+64,66+64+64+2+40+64+64+64+2),16) == 1;
                let isReferrer = parseInt(hex.slice(66+64+64+2+40+64+64+64+2,66+64+64+2+40+64+64+64+2+2),16) == 1;
                let converterState = hex.slice(66+64+64+2+40+64+64+64+2+2);

                converterState = this.base.web3.toUtf8(converterState);
                if(converterState == '') {
                    converterState = 'NOT_CONVERTER';
                }
                let obj : IAddressStats = {
                    amountConverterSpentETH: parseFloat(this.utils.fromWei(amountConverterSpent,'ether').toString()),
                    referrerRewards : parseFloat(this.utils.fromWei(rewards,'ether').toString()),
                    tokensBought: parseFloat(this.utils.fromWei(unitsConverterBought, 'ether').toString()),
                    isConverter: isConverter,
                    isReferrer: isReferrer,
                    isJoined: isJoined,
                    username: this.base.web3.toUtf8(username),
                    fullName: this.base.web3.toUtf8(fullname),
                    email: this.base.web3.toUtf8(email),
                    ethereumOf: ethereumof,
                    converterState: converterState
                };
                resolve(obj);
            } catch (e) {
                reject(e);
            }
        })
    }


    /**
     * Enables converter to do offline conversion
     * @param campaign
     * @param {string} from
     * @param {number} conversionAmountFiat Will be float amount of dollars (example 2345,5$)
     * @param {boolean} isConverterAnonymous
     * @param {number} gasPrice
     * @returns {Promise<string>}
     */
    public convertOffline(campaign: any, signature: string, _converter: string, from: string, conversionAmountFiat: number, {gasPrice = this.base._getGasPrice(), isConverterAnonymous}: IConvertOpts = {}) : Promise<string> {
        return new Promise<string>(async(resolve,reject) => {
            try {
                const nonce = await this.helpers._getNonce(from);
                const twoKeyAcquisitionCampaignInstance = await this._getCampaignInstance(campaign);
                conversionAmountFiat = parseFloat(this.utils.toWei(conversionAmountFiat, 'ether').toString());
                let txHash = await promisify(twoKeyAcquisitionCampaignInstance.convertFiat,[signature, _converter, conversionAmountFiat, false,
                    {
                        from,
                        gasPrice,
                        nonce
                    }]);
                resolve(txHash);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param {number} conversionId
     * @param {string} from
     * @returns {Promise<IPurchaseInformation>}
     */
    public getPurchaseInformation(campaign: any, conversionId: number, from: string) : Promise<IPurchaseInformation> {
        return new Promise<IPurchaseInformation>(async(resolve,reject) => {
            try {
                let purchasesHandler = await this._getPurchasesHandlerInstance(campaign);
                let [converter, baseTokens, bonusTokens, portionAmount, isPortionWithdrawn, unlockingDates] =
                    await promisify(purchasesHandler.getPurchaseInformation,[conversionId,{from}]);
                portionAmount = portionAmount.filter(amount => parseFloat(this.utils.fromWei(amount, 'ether').toString()));
                baseTokens = parseFloat(this.utils.fromWei(baseTokens, 'ether').toString());
                bonusTokens = parseFloat(this.utils.fromWei(bonusTokens, 'ether').toString());
                let obj: IPurchaseInformation = {
                    campaignAddress: campaign.address || campaign,
                    converter,
                    conversionId,
                    vestingPortions: portionAmount.length,
                    baseTokens,
                    bonusTokens,
                    totalTokens: baseTokens + bonusTokens,
                    baseLocked: Date.now() < parseInt(unlockingDates[0], 10) * 1000,
                    contracts: isPortionWithdrawn.map((withdrawn, index) => ({
                        withdrawn,
                        amount: parseFloat(this.utils.fromWei(portionAmount[index], 'ether').toString()),
                        unlockDate: new Date(parseInt(unlockingDates[index], 10) * 1000),
                        unlockTimestamp: parseInt(unlockingDates[index], 10),
                        lockupsAddress: purchasesHandler.address,
                    })),
                    portionAmount: portionAmount.map(amount => amount.toString()),
                    isPortionWithdrawn,
                    unlockingDays: unlockingDates.map(date => parseInt(date.toString(), 10)),
                };
                resolve(obj);
            } catch (e) {
                reject(e);
            }
        })
    }


    /**
     * Method to withdraw tokens, converter is sending which part he wants to withdraw - only converter can call this
     * @param {string} twoKeyLockup
     * @param {number} part
     * @param {string} from
     * @returns {Promise<string>}
     */
    public withdrawTokens(campaign: any, conversionId: number, portion: number, from:string, gasPrice: number = this.base._getGasPrice()) : Promise<string> {
        return new Promise(async(resolve,reject) => {
            try {
                const twoKeyPurchasesHandlerInstance = await this._getPurchasesHandlerInstance(campaign);
                const txHash = await promisify(twoKeyPurchasesHandlerInstance.withdrawTokens, [
                    conversionId,
                    portion,
                    {
                        from,
                        gasPrice
                    }
                ]);
                // await this.utils.getTransactionReceiptMined(txHash);
                resolve(txHash);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @returns {Promise<string>}
     */
    public getBoughtTokensReleaseDates(campaign:any) : Promise<string> {
        return new Promise(async(resolve,reject) => {
            try {
                const twoKeyPurchasesHandlerInstance = await this._getPurchasesHandlerInstance(campaign);
                let dates = await promisify(twoKeyPurchasesHandlerInstance.getPortionsUnlockingDates,[]);
                resolve(dates.map(date => parseInt(date.toString(),10)));
            } catch (e) {
                reject(e);
            }
        })
    }


    public getRateAtWhich2KEYWasBoughtFIAT(campaign: any) : Promise<number> {
        return new Promise<number>(async(resolve,reject) => {
            try {
                let campaignInstance = await this._getCampaignInstance(campaign);
                let rate = await promisify(campaignInstance.usd2KEYrateWei,[]);
                resolve(parseFloat(this.utils.fromWei(rate,'ether').toString()));
            } catch (e) {
                reject(e);
            }
        })
    }
    /**
     *
     * @param {boolean} acceptsFiat
     * @param {boolean} acceptsEther
     * @param {number} hardCap
     * @param {number} maxReferralReward
     * @returns {number}
     */
    public getRequiredRewardsInventoryAmount(acceptsFiat: boolean, acceptsEther: boolean, hardCap: number, maxReferralRewardPercent: number) : Promise<IRequiredRewards > {
        return new Promise<IRequiredRewards>(async(resolve,reject) => {
            try {
                //We assume HARDCAP is always in USD, 2key price is also always in USD, maxReferralReward is regular number, not wei's
                // This will return how much 1 ether is worth USD
                let etherToUsd = await promisify(this.base.twoKeyExchangeContract.getBaseToTargetRate,["USD"]);
                etherToUsd = parseFloat(this.utils.fromWei(etherToUsd,'ether').toString());

                if(acceptsEther == true) {
                    resolve({
                        'amount2key': 0,
                        'ethWorth2keys': 0
                    });
                    return;
                }

                if(acceptsFiat == true) {
                    let usdAmount = hardCap * (maxReferralRewardPercent/100);
                    let [amount2key,,] = await promisify(this.base.twoKeyUpgradableExchange.get2KEYTokenPriceAndAmountOfTokensReceiving,[usdAmount*(Math.pow(10,18))]);
                    amount2key = parseFloat(this.utils.fromWei(amount2key,'ether').toString());
                    let ethWorth2keys = usdAmount / etherToUsd;
                    // Add 0.1% on top due to rates difference
                    amount2key = amount2key * 1.001;
                    resolve({amount2key, ethWorth2keys});
                    return;
                }
            } catch (e) {
                reject(e);
            }
        })

    }

    /**
     *
     * @param campaign
     * @param {number} amountToBeReserved
     * @param {string} from
     * @returns {Promise<string>}
     */
    public specifyFiatConversionRewards(campaign: any, value: number, amountToBeReserved: number, from: string, gasPrice: number = this.base._getGasPrice()) : Promise<string> {
        return new Promise<string>(async(resolve,reject) => {
            try {
                const campaignInstance = await this._getCampaignInstance(campaign);
                const nonce = await this.helpers._getNonce(from);

                let txHash = await promisify(campaignInstance.specifyFiatConversionRewards, [
                    {
                        from,
                        gasPrice,
                        value,
                        nonce,
                    }]);
                resolve(txHash);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param {string} link
     * @param {string} plasmaAddress
     * @returns {Promise<string>}
     */
    public getSignatureFromLink(link: string, plasmaAddress: string, fSecret?: string) : Promise<string> {
        return new Promise<string>(async(resolve,reject) => {
            try {
                let signature;
                const offchainData = await this.base.ipfs.get(link, { json: true });
                if (!offchainData) {
                    resolve("ERROR");
                } else {
                    const { f_address, f_secret = fSecret, p_message } = offchainData;
                    signature = this.sign.free_take(plasmaAddress, f_address, f_secret, p_message);
                    const x = this.sign.validate_join(null, null, null, signature, plasmaAddress);
                    resolve(signature);
                }
            } catch (e) {
                reject(e);
            }
        })
    }


    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<boolean>}
     */
    public activateCampaign(campaign:any, from: string, gasPrice: number = this.base._getGasPrice()) : Promise<string> {
        return new Promise<string>(async(resolve,reject) => {
            try {
                let campaignInstance = await this._getLogicHandlerInstance(campaign);
                let txHash = await promisify(campaignInstance.activateCampaign, [
                    {
                        from,
                        gasPrice
                    }
                ]);
                resolve(txHash);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @returns {Promise<boolean>}
     */
    public isCampaignActivated(campaign: any) : Promise<boolean> {
        return new Promise<boolean>(async(resolve,reject) => {
            try {
                let campaignInstance = await this._getLogicHandlerInstance(campaign);
                let isActivated = await promisify(campaignInstance.IS_CAMPAIGN_ACTIVE,[]);
                resolve(isActivated);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<string>}
     */
    public withdrawUnsoldTokens(campaign:any, from: string, gasPrice: number = this.base._getGasPrice()) : Promise<string> {
        return new Promise<string>(async(resolve,reject) => {
            try {
                let campaignInstance = await this._getCampaignInstance(campaign);
                let withdrawUnsoldTokensCalled = await promisify(campaignInstance.withdrawUnsoldTokensCalled,[]);
                if(!withdrawUnsoldTokensCalled) {
                    let txHash = await promisify(campaignInstance.withdrawUnsoldTokens,[
                        {
                            from,
                            gasPrice
                        }
                    ]);
                    resolve(txHash);
                } else {
                    reject(new Error("Withdraw unsold tokens function have been called already!"));
                }

            } catch (e) {
                reject(e);
            }
        })
    }


    /**
     *
     * @param campaign
     * @param {string} from
     * @returns {Promise<boolean>}
     */
    public canContractorWithdrawUnsoldTokens(campaign:any, from: string) : Promise<boolean> {
        return new Promise<boolean>(async(resolve,reject) => {
            try {
                let logicHandlerInsance = await this._getLogicHandlerInstance(campaign);
                let status = await promisify(logicHandlerInsance.canContractorWithdrawUnsoldTokens,[{from}]);
                resolve(status);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} referrerAddress
     * @param {string} from
     * @returns {Promise<any>}
     */
    public getAmountReferrerCanWithdraw(campaign: any, referrerAddress: string, from: string) : Promise<any> {
        return new Promise<any>(async(resolve,reject) => {
            try {
                let campaignInstance = await this._getCampaignInstance(campaign);
                let balanceTwoKeys = await promisify(campaignInstance.getReferrerPlasmaBalance,[referrerAddress, {from}]);

                let contractId = await promisify(this.base.twoKeyUpgradableExchange.getContractId,[campaign,{from}]);


                let stableCoinWorth2key = await promisify(this.base.twoKeyUpgradableExchange.getUSDStableCoinAmountFrom2keyUnits,[balanceTwoKeys, contractId]);


                resolve({
                    balance2key: parseFloat(this.utils.fromWei(balanceTwoKeys,'ether').toString()),
                    balanceDAI: parseFloat(this.utils.fromWei(stableCoinWorth2key,'ether').toString())
                });
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} converterAddress
     * @returns {Promise<any>}
     */
    public getConverterPurchasesStats(campaign: any, converterAddress: string) : Promise <any> {
        return new Promise<any>(async(resolve,reject) => {
            let conversionHandler = await this._getConversionHandlerInstance(campaign);
            let [amountSpentEth, amountSpentFiat, unitsBought] = await promisify(conversionHandler.getConverterPurchasesStats,[converterAddress]);
            resolve({
                amountSpentEth: amountSpentEth,
                amountSpentFiat: amountSpentFiat,
                unitsBought: unitsBought
            })
        })
    }

    /**
     *
     * @param campaign
     * @param {string} converterAddress
     * @returns {Promise<any>}
     */
    public getConverterMetricsPerCampaign(campaign:any, converterAddress: string) : Promise<any> {
        return new Promise<any>(async(resolve,reject) => {
            try {
                let purchasesHandler = await this._getPurchasesHandlerInstance(campaign);
                let [totalBought, totalAvailable, totalLocked, totalWithdrawn] = await promisify(purchasesHandler.getMetricsPerConverterPerCampaign,[converterAddress]);
                resolve({
                    totalBought : parseFloat(this.utils.fromWei(totalBought,'ether').toString()),
                    totalAvailable: parseFloat(this.utils.fromWei(totalAvailable,'ether').toString()),
                    totalLocked: parseFloat(this.utils.fromWei(totalLocked,'ether').toString()),
                    totalWithdrawn: parseFloat(this.utils.fromWei(totalWithdrawn,'ether').toString())
                })
            } catch (e) {
                reject(e);
            }
        })
    }

    public getNumberOfInfluencersForConverter(campaign:any, converterAddress: string) : Promise<number> {
        return new Promise<number>(async(resolve,reject) => {
            try {
                let campaignInstance = await this._getCampaignInstance(campaign);
                let numberOfInfluencers = await promisify(campaignInstance.getNumberOfUsersToContractor,[converterAddress]);
                resolve(parseInt(numberOfInfluencers,10));
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} converterAddress
     * @returns {Promise<string[]>}
     */
    public getReferrersForConverter(campaign:any, converterAddress: string) : Promise<string[]> {
        return new Promise<string[]>(async(resolve,reject) => {
            try {
                let campaignInstance = await this._getLogicHandlerInstance(campaign);
                let referrers = await promisify(campaignInstance.getReferrers,[converterAddress]);
                resolve(referrers);
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     * @param {string} referrerAddress
     * @returns {Promise<number>}
     */
    public getReferrerPlasmaBalance(campaign: any, referrerAddress: string) : Promise<number> {
        return new Promise<number>(async(resolve,reject) => {
            try {
                let campaignInstance = await this._getCampaignInstance(campaign);
                let referrerBalance = await promisify(campaignInstance.getReferrerPlasmaBalance,[referrerAddress]);
                resolve(parseFloat(this.utils.fromWei(referrerBalance,'ether').toString()));
            } catch (e) {
                reject(e);
            }
        })
    }

    /**
     *
     * @param campaign
     */
    public checkIfRewardsAreBoughtWithEther(campaign:any) : Promise<boolean> {
        return new Promise<boolean>(async(resolve,reject) => {
            try {
                let campaignInstance = await this._getCampaignInstance(campaign);
                let areRewardsBoughtWithEther = await promisify(campaignInstance.boughtRewardsWithEther,[]);
                resolve(areRewardsBoughtWithEther);
            } catch (e) {
                reject(e);
            }
        })
    }

}
